import React, { useEffect, Suspense } from "react";

import { FETCH_EMPLOYEES } from "EmployerApplication/Actions";
import { ApplicationLayout } from "EmployerApplication/Components";
import { getRecommendationSurveyByCompanyProfileId } from "EntitiesApi/api";
import { requestDentalPlans, requestVisionPlans, requestLifePlans, RECOMMENDATION_SURVEY_LOADED } from "QuotePage/Actions";
import { useDispatch } from "react-redux";
import { Switch, Route } from "react-router-dom";
import { BasicLoading } from "ZeroRuby";

import PartnerProvider from "Common/PartnerProvider";
import ScrollToTop from "Common/ScrollToTop";
import { ThemeProvider } from "Theme/Context";

import { useGetCurrentCompanyApplicationQuery } from "./api";
import {
  CompanyInformationPage,
  CoverageOptionsPage,
  CreateAccountPage,
  AddEmployeesPage,
  SelectContributionTiersPage,
  ReviewEmployeeEmailsPage,
  DocumentsUploadPage,
  AddPaymentInformationPage,
  ApplicationStatusPage,
} from "./pages";
import { nestedPrefixRoutes } from "./urls";

const useLoadData = () => {
  const { data: { companyProfileId, companyIdToken } = {} } = useGetCurrentCompanyApplicationQuery();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!companyIdToken) return;
    dispatch(requestDentalPlans({ companyIdToken }));
    dispatch(requestVisionPlans({ companyIdToken }));
    dispatch(requestLifePlans({ companyIdToken }));
    dispatch({
      type: "REQUEST_ER_APP_INITIAL_LOAD",
    });
    dispatch({ type: FETCH_EMPLOYEES, payload: false });
  }, [companyIdToken]);

  useEffect(() => {
    if (!companyProfileId) return;
    dispatch(
      getRecommendationSurveyByCompanyProfileId(companyProfileId, {
        onSuccess: (_response, dispatch) => {
          dispatch({ type: RECOMMENDATION_SURVEY_LOADED });
        },
      }),
    );
  }, [companyProfileId]);
};

export const ModularEmployerApp = () => {
  useLoadData();

  return (
    <ThemeProvider cobalt>
      <ScrollToTop>
        <PartnerProvider>
          <EmployerAppRoutes />
        </PartnerProvider>
      </ScrollToTop>
    </ThemeProvider>
  );
};

const routes = [
  { path: "/employer-application/coverage-options", Component: CoverageOptionsPage, withQuotePanel: false },
  { path: "/employer-application/company-information", Component: CompanyInformationPage, withQuotePanel: true },
  { path: "/employer-application/create-account", Component: CreateAccountPage, withQuotePanel: false },
  { path: "/employer-application/add-employees", Component: AddEmployeesPage, withQuotePanel: true },
  { path: "/employer-application/select-contribution-tiers", Component: SelectContributionTiersPage, withQuotePanel: true },
  { path: "/employer-application/send-employee-emails", Component: ReviewEmployeeEmailsPage, withQuotePanel: true },
  { path: "/employer-application/documents/upload", Component: DocumentsUploadPage, withQuotePanel: true },
  { path: "/employer-application/payment", Component: AddPaymentInformationPage, withQuotePanel: true },
  { path: "/employer-application/done", Component: ApplicationStatusPage, withQuotePanel: true },
] as const;

export const EmployerAppRoutes = () => (
  <Suspense fallback={<BasicLoading />}>
    <Switch>
      {routes.map(({ path, Component, withQuotePanel }) => (
        <Route key={path} exact path={nestedPrefixRoutes(path)}>
          <ApplicationLayout withQuotePanel={withQuotePanel} isRenewal={false}>
            <Component />
          </ApplicationLayout>
        </Route>
      ))}
    </Switch>
  </Suspense>
);
